import type { MouseEventHandler, ReactNode } from 'react';

export interface ButtonBaseProps {
  onClick?: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  title?: string;
}

export interface ClickProps {
  onClick: () => void;
}

interface CreateButtonConfig<P extends ButtonBaseProps> {
  className: string | ((props: P) => string);
  content: ReactNode | ((props: P) => ReactNode);
  type?: 'button' | 'submit';
  title?: string;
}

type ContentFn<P> = (props: P) => ReactNode;

export const createButton = <P extends ButtonBaseProps = ButtonBaseProps>(
  config: CreateButtonConfig<P>,
) => {
  const resolveClassName =
    typeof config.className === 'string'
      ? () => config.className as string
      : config.className;

  const resolveContent: ContentFn<P> =
    typeof config.content === 'function'
      ? (config.content as ContentFn<P>)
      : () => config.content as ReactNode;

  const type = config.type ?? 'button';
  const defaultTitle = config.title;

  return (props: P) => (
    <button
      type={type}
      onClick={props.onClick}
      disabled={props.disabled}
      title={props.title ?? defaultTitle}
      className={resolveClassName(props)}
    >
      {resolveContent(props)}
    </button>
  );
};
